/**
 * Message Validator for WebSocket Notifications
 * 
 * Phase 2: Message Processing
 * 
 * Validates incoming WebSocket messages to ensure data integrity
 * and prevent processing of malformed or malicious messages.
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Message validation error types
 */
export const ValidationErrorType = {
  INVALID_JSON: 'INVALID_JSON',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_TYPE: 'INVALID_TYPE',
  INVALID_VALUE: 'INVALID_VALUE',
  SCHEMA_MISMATCH: 'SCHEMA_MISMATCH'
};

/**
 * Notification message types
 */
export const NotificationType = {
  NOTIFICATION: 0,
  WALLET: 1,
  ORDER: 2
};

/**
 * Message validation schema
 */
const messageSchema = {
  // Required fields for all messages
  required: ['type'],
  
  // Optional fields that may be present
  optional: [
    'id',
    'title',
    'message',
    'content',
    'body',
    'en_body',
    'section',
    'status',
    'created_at',
    'update_wallet',
    'update_orders',
    'silent',
    'hash'
  ],
  
  // Type-specific required fields
  typeSpecific: {
    [NotificationType.NOTIFICATION]: {
      required: ['title'],
      optional: ['message', 'content', 'section', 'silent', 'update_wallet', 'update_orders']
    },
    [NotificationType.WALLET]: {
      required: ['id'],
      optional: ['amount', 'freeze', 'value', 'toman_amount']
    },
    [NotificationType.ORDER]: {
      required: ['id'],
      optional: ['time', 'market', 'order_type', 'role', 'order_price', 'amount', 'total']
    }
  }
};

/**
 * Validate message structure
 * 
 * @param {any} message - Raw message to validate
 * @returns {Object} Validation result { valid: boolean, errors: Array, data: Object }
 */
export function validateMessage(message) {
  const errors = [];
  
  // 1. Check if message is an object
  if (typeof message !== 'object' || message === null) {
    return {
      valid: false,
      errors: [{
        type: ValidationErrorType.INVALID_TYPE,
        field: 'message',
        message: 'Message must be an object',
        value: message
      }],
      data: null
    };
  }
  
  // 2. Check required fields
  for (const field of messageSchema.required) {
    if (!(field in message)) {
      errors.push({
        type: ValidationErrorType.MISSING_REQUIRED_FIELD,
        field: field,
        message: `Required field '${field}' is missing`
      });
    }
  }
  
  // 3. Validate type field
  if ('type' in message) {
    const type = message.type;
    const validTypes = [
      NotificationType.NOTIFICATION,
      NotificationType.WALLET,
      NotificationType.ORDER,
      'NOTIFICATION',
      'WALLET',
      'ORDER'
    ];
    
    if (!validTypes.includes(type)) {
      errors.push({
        type: ValidationErrorType.INVALID_VALUE,
        field: 'type',
        message: `Invalid message type: ${type}`,
        value: type
      });
    }
  }
  
  // 4. Validate type-specific fields
  if ('type' in message && !errors.length) {
    const messageType = typeof message.type === 'string' 
      ? message.type.toUpperCase() 
      : message.type;
    
    const numericType = typeof messageType === 'string'
      ? NotificationType[messageType]
      : messageType;
    
    const typeSchema = messageSchema.typeSpecific[numericType];
    
    if (typeSchema) {
      // Check type-specific required fields
      for (const field of typeSchema.required) {
        if (!(field in message)) {
          errors.push({
            type: ValidationErrorType.MISSING_REQUIRED_FIELD,
            field: field,
            message: `Required field '${field}' for type ${messageType} is missing`
          });
        }
      }
    }
  }
  
  // 5. Validate boolean flags
  const booleanFields = ['update_wallet', 'update_orders', 'silent'];
  for (const field of booleanFields) {
    if (field in message && typeof message[field] !== 'boolean') {
      errors.push({
        type: ValidationErrorType.INVALID_TYPE,
        field: field,
        message: `Field '${field}' must be a boolean`,
        value: message[field]
      });
    }
  }
  
  // 6. Validate numeric fields
  const numericFields = ['id', 'amount', 'freeze', 'value', 'toman_amount', 'status'];
  for (const field of numericFields) {
    if (field in message && message[field] !== null && typeof message[field] !== 'number') {
      // Try to parse as number
      const parsed = parseFloat(message[field]);
      if (isNaN(parsed)) {
        errors.push({
          type: ValidationErrorType.INVALID_TYPE,
          field: field,
          message: `Field '${field}' must be a number`,
          value: message[field]
        });
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors: errors,
    data: errors.length === 0 ? message : null
  };
}

/**
 * Validate JSON string and parse
 * 
 * @param {string} jsonString - JSON string to validate and parse
 * @returns {Object} Validation result { valid: boolean, errors: Array, data: Object }
 */
export function validateAndParseJSON(jsonString) {
  try {
    const parsed = JSON.parse(jsonString);
    return validateMessage(parsed);
  } catch (error) {
    return {
      valid: false,
      errors: [{
        type: ValidationErrorType.INVALID_JSON,
        field: 'message',
        message: `Invalid JSON: ${error.message}`,
        error: error
      }],
      data: null
    };
  }
}

/**
 * Sanitize message data
 * Remove potentially dangerous fields and normalize values
 * 
 * @param {Object} message - Message to sanitize
 * @returns {Object} Sanitized message
 */
export function sanitizeMessage(message) {
  if (!message || typeof message !== 'object') {
    return message;
  }
  
  const sanitized = { ...message };
  
  // Remove potentially dangerous fields
  const dangerousFields = ['__proto__', 'constructor', 'prototype'];
  for (const field of dangerousFields) {
    delete sanitized[field];
  }
  
  // Sanitize string fields (prevent XSS)
  const stringFields = ['title', 'message', 'content', 'body', 'en_body', 'section'];
  for (const field of stringFields) {
    if (field in sanitized && typeof sanitized[field] === 'string') {
      // Remove script tags and event handlers
      sanitized[field] = sanitized[field]
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/javascript:/gi, '');
    }
  }
  
  return sanitized;
}

/**
 * Check if message is a duplicate based on hash
 * 
 * @param {Object} message - Message to check
 * @param {Set} processedHashes - Set of already processed message hashes
 * @returns {boolean} True if duplicate, false otherwise
 */
export function isDuplicateMessage(message, processedHashes) {
  if (!message || !message.hash) {
    return false;
  }
  
  return processedHashes.has(message.hash);
}

/**
 * Message validation statistics
 */
export class MessageValidationStats {
  constructor() {
    this.reset();
  }
  
  reset() {
    this.totalMessages = 0;
    this.validMessages = 0;
    this.invalidMessages = 0;
    this.duplicateMessages = 0;
    this.errorsByType = {};
    this.lastReset = Date.now();
  }
  
  recordValid() {
    this.totalMessages++;
    this.validMessages++;
  }
  
  recordInvalid(errors) {
    this.totalMessages++;
    this.invalidMessages++;
    
    for (const error of errors) {
      const type = error.type;
      this.errorsByType[type] = (this.errorsByType[type] || 0) + 1;
    }
  }
  
  recordDuplicate() {
    this.totalMessages++;
    this.duplicateMessages++;
  }
  
  getStats() {
    const uptime = Date.now() - this.lastReset;
    const uptimeMinutes = Math.floor(uptime / 60000);
    
    return {
      totalMessages: this.totalMessages,
      validMessages: this.validMessages,
      invalidMessages: this.invalidMessages,
      duplicateMessages: this.duplicateMessages,
      validRate: this.totalMessages > 0 
        ? ((this.validMessages / this.totalMessages) * 100).toFixed(2) + '%'
        : '0%',
      invalidRate: this.totalMessages > 0
        ? ((this.invalidMessages / this.totalMessages) * 100).toFixed(2) + '%'
        : '0%',
      duplicateRate: this.totalMessages > 0
        ? ((this.duplicateMessages / this.totalMessages) * 100).toFixed(2) + '%'
        : '0%',
      errorsByType: this.errorsByType,
      uptime: uptimeMinutes > 0 ? `${uptimeMinutes} minutes` : `${Math.floor(uptime / 1000)} seconds`,
      messagesPerMinute: uptimeMinutes > 0
        ? (this.totalMessages / uptimeMinutes).toFixed(2)
        : '0'
    };
  }
}

